import type { SiteSettings } from './types';

export interface HomepageHeroContent {
  badge: string;
  title: string;
  subtitle: string;
  imageUrl: string;
  primaryCtaText: string;
  primaryCtaLink: string;
  secondaryCtaText: string;
  secondaryCtaLink: string;
  eyebrowItems: string[];
}

export interface HomepageStatContent {
  value: string;
  label: string;
  icon: 'users' | 'shield' | 'sprout' | 'heart';
}

export interface HomepageFocusAreaContent {
  title: string;
  description: string;
  imageUrl: string;
}

export interface HomepageTestimonialContent {
  quote: string;
  name: string;
  role: string;
}

export interface HomepageContent {
  hero: HomepageHeroContent;
  stats: HomepageStatContent[];
  focusAreasHeading: string;
  focusAreasSubtitle: string;
  focusAreas: HomepageFocusAreaContent[];
  testimonialsHeading: string;
  testimonialsSubtitle: string;
  testimonials: HomepageTestimonialContent[];
  newsHeading: string;
  newsSubheading: string;
  ctaHeading: string;
  ctaSubtitle: string;
  ctaButtonText: string;
  ctaButtonLink: string;
}

export function getDefaultHomePageContent(): HomepageContent {
  return {
    hero: {
      badge: 'A Maasai community initiative for change',
      title: 'Ending FGM.\nEmpowering Women.\nGrowing Together.',
      subtitle:
        'Kisapuk Community Initiative works hand-in-hand with Maasai communities to end female genital mutilation and gender-based violence, teach women sustainable farming, and share stories of hope and resilience.',
      imageUrl: 'https://images.unsplash.com/photo-1509099836639-18ba1795216d?auto=format&fit=crop&w=1600&q=80',
      primaryCtaText: 'Explore Our Work',
      primaryCtaLink: '/programs',
      secondaryCtaText: 'Get Involved',
      secondaryCtaLink: '/contact',
      eyebrowItems: ['Community Outreach', 'Women’s Education', 'Health Services', 'Economic Empowerment'],
    },
    stats: [
      { value: '3,200+', label: 'Women & Girls Empowered', icon: 'users' },
      { value: '48', label: 'Villages FGM-Free', icon: 'shield' },
      { value: '1,500', label: 'Women Trained in Farming', icon: 'sprout' },
      { value: '12,000', label: 'Lives Touched', icon: 'heart' },
    ],
    focusAreasHeading: 'Our Focus Areas',
    focusAreasSubtitle:
      'We work at the intersection of rights, livelihoods, and storytelling — addressing the root causes of inequality through community-driven solutions.',
    focusAreas: [
      {
        title: 'Against FGM & GBV',
        description:
          'Working with elders, circumcisers, and survivors to end female genital mutilation and gender-based violence through dialogue, education, and alternative rites of passage.',
        imageUrl: 'https://images.pexels.com/photos/6646917/pexels-photo-6646917.jpeg',
      },
      {
        title: 'Women in Farming',
        description:
          'Teaching women climate-smart agriculture, poultry keeping, and kitchen gardens so they can feed their families and earn a sustainable income.',
        imageUrl: 'https://images.pexels.com/photos/4503267/pexels-photo-4503267.jpeg',
      },
      {
        title: 'Community News',
        description:
          'Sharing stories of change, resilience, and hope from across the community — amplifying voices that are too often unheard.',
        imageUrl: 'https://images.pexels.com/photos/5184442/pexels-photo-5184442.jpeg',
      },
      {
        title: 'Health & Wellness',
        description:
          'Maternal care, reproductive health education, and psychosocial support for survivors of gender-based violence.',
        imageUrl: 'https://images.pexels.com/photos/6646917/pexels-photo-6646917.jpeg',
      },
    ],
    testimonialsHeading: 'Stories From Our Community',
    testimonialsSubtitle: 'Real people. Real change. Real hope.',
    testimonials: [
      {
        quote:
          'Before Kisapuk came to our village, I thought FGM was our culture and could not change. Today, I am a champion for girls\' rights and my daughter will not be cut.',
        name: 'Nashipai Saen',
        role: 'Community Champion, Narok',
      },
      {
        quote:
          'The farming training changed my life. I now grow enough vegetables to feed my children and sell the surplus at the market. I am no longer dependent on anyone.',
        name: 'Rebecca Kuntai',
        role: 'Farmer & Mother of Four',
      },
      {
        quote:
          'As an elder, I once believed cutting was our way. The dialogue sessions opened my eyes. Today I speak against FGM in every gathering I attend.',
        name: 'Mzee Lemayian',
        role: 'Village Elder, Transmara',
      },
    ],
    newsHeading: 'News & Stories',
    newsSubheading: 'Follow the latest work, community stories, and progress from Kisapuk Community Initiative.',
    ctaHeading: 'Ready to Stand With Us?',
    ctaSubtitle:
      'Join us in building a future where every girl is safe, every woman thrives, and every voice is heard.',
    ctaButtonText: 'Contact Us Today',
    ctaButtonLink: '/contact',
  };
}

export function getHomePageContent(settings?: SiteSettings): HomepageContent {
  const raw = settings?.home_page_content;
  if (!raw) {
    return getDefaultHomePageContent();
  }

  try {
    const parsed = JSON.parse(raw) as Partial<HomepageContent>;
    const defaults = getDefaultHomePageContent();

    return {
      ...defaults,
      ...parsed,
      hero: {
        ...defaults.hero,
        ...parsed.hero,
        eyebrowItems: Array.isArray(parsed.hero?.eyebrowItems) && parsed.hero.eyebrowItems.length > 0
          ? parsed.hero.eyebrowItems
          : defaults.hero.eyebrowItems,
      },
      stats: Array.isArray(parsed.stats) && parsed.stats.length > 0
        ? parsed.stats.map((stat) => ({
            value: stat?.value || '',
            label: stat?.label || '',
            icon: (stat?.icon as HomepageStatContent['icon']) || 'users',
          }))
        : defaults.stats,
      focusAreas: Array.isArray(parsed.focusAreas) && parsed.focusAreas.length > 0
        ? parsed.focusAreas.map((area) => ({
            title: area?.title || '',
            description: area?.description || '',
            imageUrl: area?.imageUrl || '',
          }))
        : defaults.focusAreas,
      testimonials: Array.isArray(parsed.testimonials) && parsed.testimonials.length > 0
        ? parsed.testimonials.map((testimonial) => ({
            quote: testimonial?.quote || '',
            name: testimonial?.name || '',
            role: testimonial?.role || '',
          }))
        : defaults.testimonials,
    };
  } catch {
    return getDefaultHomePageContent();
  }
}

export function stringifyHomePageContent(content: HomepageContent): string {
  return JSON.stringify(content, null, 2);
}
